// ============================================================================
// Shared Utilities - Common calculations and formatters
// ============================================================================

/**
 * Convert a month number to its corresponding name.
 *
 * @param {number|string} month - Month number (1-12). Can be string or number.
 * @param {string} [format='long'] - Output format: 'long' (January) or 'short' (Jan)
 * @returns {string} Month name, or the original value as string if invalid
 * @example
 * getMonthName(1);           // 'January'
 * getMonthName(1, 'short');  // 'Jan'
 * getMonthName('12');        // 'December'
 * getMonthName(13);          // '13' (invalid, returns original)
 */
export function getMonthName(month, format = 'long') {
    const monthNum = typeof month === 'string' ? parseInt(month) : month;

    if (monthNum < 1 || monthNum > 12 || isNaN(monthNum)) {
        return month.toString(); // Return original if invalid
    }

    const date = new Date(2024, monthNum - 1, 1);
    return date.toLocaleString('default', { month: format });
}

/**
 * Format a period value for human-readable display.
 * Supports various period formats including monthly, yearly, and provisional.
 *
 * @param {string|number} value - Period value (e.g., '202401', '2024', '2026P001')
 * @param {string} type - Period type: 'YYYYMM' | 'YYYY' | 'PROVISIONAL' | 'CUSTOM'
 * @returns {string} Formatted period string, or original value if format is unrecognized
 * @example
 * formatPeriod('202401', 'YYYYMM');      // 'Jan 2024'
 * formatPeriod('2024', 'YYYY');          // '2024'
 * formatPeriod('2026P001', 'PROVISIONAL'); // '2026 Provisional 001'
 * formatPeriod('2026-Website Redesign', 'CUSTOM'); // '2026 - Website Redesign'
 * formatPeriod(null, 'YYYYMM');          // ''
 */
export function formatPeriod(value, type) {
    if (!value) return '';

    const valueStr = value.toString();

    switch (type) {
        case 'YYYYMM': {
            if (valueStr.length !== 6) return valueStr;
            const year = valueStr.substring(0, 4);
            const month = valueStr.substring(4, 6);
            const monthName = getMonthName(month, 'short');
            return `${monthName} ${year}`;
        }

        case 'YYYY':
            return valueStr;

        case 'PROVISIONAL': {
            // Format: 2026P001 -> "2026 Provisional 001"
            if (valueStr.length !== 8 || valueStr[4] !== 'P') return valueStr;
            const provYear = valueStr.substring(0, 4);
            const provNumber = valueStr.substring(5, 8);
            return `${provYear} Provisional ${provNumber}`;
        }

        case 'CUSTOM': {
            // Format: "2026-Website Redesign" -> "2026 - Website Redesign"
            const dashIdx = valueStr.indexOf('-');
            if (dashIdx === 4) {
                const customYear = valueStr.substring(0, 4);
                const description = valueStr.substring(5);
                return `${customYear} - ${description}`;
            }
            return valueStr;
        }

        default:
            return valueStr;
    }
}

// ============================================================================
// Date Formatting Functions
// ============================================================================

/**
 * Safely convert any date input to a Date object.
 * Handles Date objects, ISO strings, and timestamps.
 * For ISO date-only strings (YYYY-MM-DD), parses in local timezone to avoid
 * the common bug where "2025-08-08" becomes August 7 due to UTC parsing.
 *
 * @param {string|Date} date - Date input
 * @returns {Date|null} Date object or null if invalid
 */
export function toDateObj(date) {
    if (!date) return null;
    if (date instanceof Date) return isNaN(date.getTime()) ? null : date;
    if (typeof date !== 'string') return null;

    // For YYYY-MM-DD strings, parse in local timezone
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        const [y, m, d] = date.split('-').map(Number);
        if (m < 1 || m > 12 || d < 1 || d > 31) return null;
        const obj = new Date(y, m - 1, d, 0, 0, 0, 0);
        return isNaN(obj.getTime()) ? null : obj;
    }

    const obj = new Date(date);
    return isNaN(obj.getTime()) ? null : obj;
}

/**
 * Format a date for display in various formats.
 * Handles both Date objects and ISO date strings.
 *
 * @param {string|Date} date - Date to format. Accepts Date objects or ISO date strings.
 * @param {string} [format='display'] - Output format:
 *   - 'display': DD/MM/YYYY (e.g., '15/01/2024') - user's preferred format
 *   - 'iso': YYYY-MM-DD (e.g., '2024-01-15') - for HTML inputs and API
 *   - 'medium': Short month (e.g., '15 Jan 2024') - for readable labels
 *   - 'long': Full month (e.g., '15 January 2024') - for headers/reports
 *   - 'short': Compact (e.g., '15/01/24') - for changelogs/audit
 *   - 'datetime': Date + time (e.g., '15 Jan 2024, 14:30')
 *   - 'dayLabel': Day + date (e.g., 'Mon, 15 Jan') - for timesheet columns
 *   - 'dayMonth': Day + month (e.g., '15 Jan') - for compact date ranges
 *   - 'weekdayMedium': Full context (e.g., 'Mon, 15 Jan 2024') - for leave dates
 *   - 'monthYear': Month + year (e.g., 'Jan 2024') - for period labels
 *   - 'monthYearLong': Full month + year (e.g., 'January 2024')
 * @returns {string} Formatted date string, or empty string if invalid
 * @example
 * formatDate('2024-01-15');               // '15/01/2024'
 * formatDate('2024-01-15', 'iso');        // '2024-01-15'
 * formatDate('2024-01-15', 'medium');     // '15 Jan 2024'
 * formatDate('2024-01-15', 'long');       // '15 January 2024'
 * formatDate('2024-01-15', 'datetime');   // '15 Jan 2024, 00:00'
 * formatDate(null);                       // ''
 */
export function formatDate(date, format = 'display') {
    const dateObj = toDateObj(date);
    if (!dateObj) return '';

    const dd = String(dateObj.getDate()).padStart(2, '0');
    const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
    const yyyy = dateObj.getFullYear();

    switch (format) {
        case 'display':
            return `${dd}/${mm}/${yyyy}`;

        case 'iso':
            return `${yyyy}-${mm}-${dd}`;

        case 'medium':
            return dateObj.toLocaleDateString('en-GB', {
                day: 'numeric', month: 'short', year: 'numeric'
            });

        case 'long':
            return dateObj.toLocaleDateString('en-GB', {
                day: 'numeric', month: 'long', year: 'numeric'
            });

        case 'short':
            return dateObj.toLocaleDateString('en-GB', {
                day: '2-digit', month: '2-digit', year: '2-digit'
            });

        case 'datetime':
            return dateObj.toLocaleString('en-GB', {
                day: 'numeric', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit'
            });

        case 'dayLabel':
            return dateObj.toLocaleDateString('en-GB', {
                weekday: 'short', month: 'short', day: 'numeric'
            });

        case 'dayMonth':
            return dateObj.toLocaleDateString('en-GB', {
                day: 'numeric', month: 'short'
            });

        case 'weekdayMedium':
            return dateObj.toLocaleDateString('en-GB', {
                weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
            });

        case 'monthYear':
            return dateObj.toLocaleDateString('en-GB', {
                month: 'short', year: 'numeric'
            });

        case 'monthYearLong':
            return dateObj.toLocaleDateString('en-GB', {
                month: 'long', year: 'numeric'
            });

        default:
            return `${dd}/${mm}/${yyyy}`;
    }
}

/**
 * Format the time portion of a date as a 24-hour HH:mm string.
 * @param {Date|string|number} date
 * @returns {string} e.g. '09:03', or '' for invalid input
 *
 * @example
 * formatTime('2024-01-15T09:03:00'); // '09:03'
 */
export function formatTime(date) {
    const dateObj = toDateObj(date);
    if (!dateObj) return '';
    return dateObj.toLocaleTimeString('en-GB', {
        hour: '2-digit', minute: '2-digit', hour12: false
    });
}

/**
 * Parse an ISO date string (YYYY-MM-DD) into a Date object without timezone conversion.
 * Prevents the common bug where "2025-08-08" becomes August 7 in local time.
 *
 * @param {string} dateISO - ISO date string in YYYY-MM-DD format
 * @returns {Date|null} Date object at midnight local time, or null if invalid
 * @example
 * parseISODate('2025-01-15');  // Date object for Jan 15 2025, midnight local
 * parseISODate('invalid');     // null
 * parseISODate(null);          // null
 */
export function parseISODate(dateISO) {
    if (!dateISO || typeof dateISO !== 'string') return null;

    const parts = dateISO.split('T')[0].split('-');
    if (parts.length !== 3) return null;

    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const day = parseInt(parts[2], 10);

    if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;

    const date = new Date(year, month - 1, day, 0, 0, 0, 0);
    if (isNaN(date.getTime())) return null;

    return date;
}

/**
 * Get today's date as YYYY-MM-DD string without timezone conversion.
 * Convenience alias for formatLocalDate() with no argument.
 *
 * @returns {string} Today's date in ISO format (YYYY-MM-DD)
 * @example
 * getTodayDateString();  // '2025-01-15'
 */
export function getTodayDateString() {
    return formatLocalDate();
}

/**
 * Format a date as YYYY-MM-DD from its LOCAL date components (no UTC conversion).
 * The safe replacement for `date.toISOString().split('T')[0]`, which converts to
 * UTC first - so a local-midnight Date in a UTC+ timezone (e.g. SAST) formats as
 * YESTERDAY's date. All "today"/"this week"/"this month" filter anchors must be
 * built with this.
 *
 * @param {Date} [date=new Date()] - Date whose local calendar date to format
 * @returns {string} Local date in YYYY-MM-DD format, or empty string if invalid
 * @example
 * formatLocalDate();                      // today's local date, e.g. '2026-07-02'
 * formatLocalDate(new Date(2026, 0, 15)); // '2026-01-15'
 */
export function formatLocalDate(date = new Date()) {
    return formatDate(date, 'iso');
}

/**
 * Get the Monday of the week containing the given date.
 * Weeks start on Monday (ISO standard).
 *
 * @param {Date} date - The reference date
 * @returns {Date} Monday at midnight local time
 * @example
 * getWeekStart(new Date('2025-01-15'));  // Date for Monday, Jan 13 2025
 */
export function getWeekStart(date) {
    const reference = new Date(date);
    const day = reference.getDay();
    const diff = reference.getDate() - day + (day === 0 ? -6 : 1);
    reference.setDate(diff);
    reference.setHours(0, 0, 0, 0);
    return reference;
}

/**
 * Parse various user-typed date input formats into YYYY-MM-DD.
 * Supports: DD/MM/YYYY, DDMMYYYY, DD-MM-YYYY, DD.MM.YYYY, YYYY-MM-DD (passthrough).
 *
 * @param {string} input - User-typed date string
 * @returns {string} YYYY-MM-DD string, or empty string if unparseable
 * @example
 * parseDateInput('15/01/2024');  // '2024-01-15'
 * parseDateInput('15012024');    // '2024-01-15'
 * parseDateInput('2024-01-15'); // '2024-01-15'
 * parseDateInput('invalid');     // ''
 */
export function parseDateInput(input) {
    if (!input) return '';
    const cleaned = input.trim();

    // Already in YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(cleaned)) return cleaned;

    let day, month, year;

    // DDMMYYYY (8 digits)
    if (/^\d{8}$/.test(cleaned)) {
        day = cleaned.substring(0, 2);
        month = cleaned.substring(2, 4);
        year = cleaned.substring(4, 8);
    }
    // DD/MM/YYYY or DD-MM-YYYY or DD.MM.YYYY
    else if (/^\d{1,2}[/\-.]\d{1,2}[/\-.]\d{2,4}$/.test(cleaned)) {
        const parts = cleaned.split(/[/\-.]/);
        day = parts[0].padStart(2, '0');
        month = parts[1].padStart(2, '0');
        year = parts[2].length === 2 ? '20' + parts[2] : parts[2];
    }
    else {
        return '';
    }

    const d = parseInt(day, 10);
    const m = parseInt(month, 10);
    const y = parseInt(year, 10);
    if (d < 1 || d > 31 || m < 1 || m > 12 || y < 1900 || y > 2100) return '';

    return `${year}-${month}-${day}`;
}

/**
 * Format a date range for compact display.
 *
 * @param {string|Date} startDate - Start date
 * @param {string|Date} endDate - End date
 * @param {object} [options] - Formatting options
 * @param {boolean} [options.weekday=false] - Include weekday names
 * @param {boolean} [options.year=false] - Always include year
 * @returns {string} Formatted date range
 * @example
 * formatDateRange('2024-01-15', '2024-01-15');  // '15 Jan'
 * formatDateRange('2024-01-15', '2024-01-20');  // '15-20 Jan'
 * formatDateRange('2024-01-15', '2024-02-10');  // '15 Jan - 10 Feb'
 * formatDateRange('2024-01-15', '2024-01-20', { weekday: true });
 *   // 'Mon, 15 Jan - Sat, 20 Jan'
 */
export function formatDateRange(startDate, endDate, options = {}) {
    const start = toDateObj(startDate);
    const end = toDateObj(endDate);
    if (!start) return '';
    if (!end) return formatDate(start, options.weekday ? 'weekdayMedium' : 'dayMonth');

    const fmtDay = (d) => {
        if (options.weekday) {
            return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
        }
        return `${d.getDate()} ${d.toLocaleDateString('en-GB', { month: 'short' })}`;
    };

    // Same day
    if (start.toDateString() === end.toDateString()) {
        if (options.weekday) {
            return start.toLocaleDateString('en-GB', {
                weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
            });
        }
        return fmtDay(start);
    }

    // Same month and year
    if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
        if (options.weekday) {
            const endOpts = { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' };
            return `${fmtDay(start)} \u2013 ${end.toLocaleDateString('en-GB', endOpts)}`;
        }
        return `${start.getDate()}-${end.getDate()} ${start.toLocaleDateString('en-GB', { month: 'short' })}`;
    }

    // Different months
    if (options.weekday) {
        const endOpts = { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' };
        return `${fmtDay(start)} \u2013 ${end.toLocaleDateString('en-GB', endOpts)}`;
    }
    return `${fmtDay(start)} - ${fmtDay(end)}`;
}

/**
 * Get date range boundaries for a given date and view mode.
 *
 * @param {Date} date - The reference date
 * @param {string} viewMode - 'day' | 'week' | 'month' | 'grid'
 * @returns {{startDate: Date, endDate: Date}} Start and end dates
 */
export function getDateRange(date, viewMode) {
    const startDate = new Date(date);
    const endDate = new Date(date);

    if (viewMode === 'day') {
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
    } else if (viewMode === 'month') {
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
        endDate.setMonth(endDate.getMonth() + 1);
        endDate.setDate(0);
        endDate.setHours(23, 59, 59, 999);
    } else {
        // week or grid - Monday to Sunday
        const ws = getWeekStart(startDate);
        startDate.setTime(ws.getTime());
        endDate.setTime(ws.getTime());
        endDate.setDate(endDate.getDate() + 6);
        endDate.setHours(23, 59, 59, 999);
    }

    return { startDate, endDate };
}

/**
 * Debounce function - delays execution until after wait milliseconds have elapsed
 * since the last time the debounced function was invoked.
 *
 * @param {Function} fn - Function to debounce
 * @param {number} delay - Wait time in milliseconds (default: 300)
 * @returns {Function} Debounced function with cancel() method
 *
 * @example
 * // Basic usage
 * const debouncedSearch = debounce((query) => fetchResults(query), 300);
 * input.addEventListener('input', (e) => debouncedSearch(e.target.value));
 *
 * @example
 * // Cancel pending execution
 * debouncedSearch.cancel();
 *
 * @example
 * // In a class method (preserves 'this' context)
 * this.debouncedSave = debounce(this.save.bind(this), 500);
 */
export function debounce(fn, delay = 300) {
    let timeoutId;

    function debounced(...args) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn.apply(this, args), delay);
    }

    /**
     * Cancel any pending debounced execution
     */
    debounced.cancel = function() {
        clearTimeout(timeoutId);
        timeoutId = null;
    };

    return debounced;
}

/**
 * Run an async worker over a list with a BOUNDED number of calls in flight,
 * resolving to one settled result per item in input order.
 *
 * `Promise.allSettled(items.map(worker))` starts everything at once, which is
 * fine for reads and wrong for writes: a Postgres function that takes a row
 * lock and fires triggers cannot absorb a whole list at once, so the calls
 * queue behind each other and the losers are cancelled on the 8s
 * `statement_timeout` the `authenticated` role carries. Same shape as
 * allSettled, so it is a drop-in - only the concurrency changes.
 *
 * @param {Array} items - The work list (never mutated)
 * @param {(item: any, index: number) => Promise<any>} worker - Called per item
 * @param {number} [concurrency=4] - Max calls in flight; clamped to >= 1
 * @returns {Promise<Array<{status: 'fulfilled'|'rejected', value?: any, reason?: any}>>}
 *     One entry per item, in INPUT order (not completion order)
 * @example
 * // Bounded writes - four in flight instead of all 46
 * const results = await mapPooled(rows, row => markAsPaid(row.taskId), 4);
 * const failed = results.filter(r => r.status === 'rejected');
 *
 * @example
 * // Strictly one at a time (a retry pass, where nothing should compete)
 * const results = await mapPooled(stragglers, row => markAsPaid(row.taskId), 1);
 */
export async function mapPooled(items, worker, concurrency = 4) {
    const list = Array.from(items || []);
    const results = new Array(list.length);
    if (!list.length) return results;

    const limit = Math.max(1, Math.min(Math.floor(concurrency) || 1, list.length));
    let cursor = 0;

    async function runner() {
        while (cursor < list.length) {
            const index = cursor++;
            try {
                results[index] = { status: 'fulfilled', value: await worker(list[index], index) };
            } catch (error) {
                results[index] = { status: 'rejected', reason: error };
            }
        }
    }

    await Promise.all(Array.from({ length: limit }, runner));
    return results;
}

/**
 * Generate a unique identifier using timestamp and random characters.
 * Useful for temporary IDs, DOM element IDs, or client-side references.
 * Note: Not suitable for cryptographic purposes or database primary keys.
 *
 * @param {string} [prefix=''] - Optional prefix to prepend to the ID
 * @returns {string} Unique ID string (e.g., 'lxyz123-abc456def' or 'task-lxyz123-abc456def')
 * @example
 * generateId();         // 'lxyz123-abc456def'
 * generateId('task');   // 'task-lxyz123-abc456def'
 * generateId('modal');  // 'modal-lxyz124-ghi789jkl'
 */
export function generateId(prefix = '') {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substr(2, 9);
    return prefix ? `${prefix}-${timestamp}-${random}` : `${timestamp}-${random}`;
}
